import React from 'react';
import { Box, Grid, Rating, Skeleton, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { differenceInCalendarDays, sub } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';

import { Row } from 'ui/Flex';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { TotalsBox } from 'ui/analytics/TotalsBox';
import { MetricCard } from 'ui/analytics/MetricCard';
import { useReviewsMetricsQueries } from '../hooks/useReviewsMetricsQueries';
import { useReviewsMetricsData } from '../hooks/useReviewsMetricsData';
import { EngagementText } from './EngagementText';

interface ReviewsMetricsSectionProps {
    dateRange: DateRangePickerValue;
    selectedLocationIds: string[];
}

const STAR_COLOR = '#FFB400';

export const ReviewsMetricsSection: React.FC<ReviewsMetricsSectionProps> = ({
    dateRange,
    selectedLocationIds
}) => {
    const {
        ratingsCountValues,
        averageRatingValues,
        totalFiveStarsValues,
        fiveStarPercentageValues,
        ratingDistributionValues,
        mobileEngagementData,
        webEngagementData
    } = useReviewsMetricsQueries(selectedLocationIds, dateRange);
    const { chartData, chartOptions, distributionPlugin } = useReviewsMetricsData({
        ratingDistribution: ratingDistributionValues?.distribution
    });

    const datePoints = React.useMemo<[Date, Date, Date]>(() => {
        const difference = differenceInCalendarDays(dateRange.endDate, dateRange.startDate);
        const retroStart = sub(dateRange.startDate, { days: difference + 1 });
        return [retroStart, dateRange.startDate, dateRange.endDate];
    }, [dateRange.endDate, dateRange.startDate]);

    const mobileEngagementText = React.useMemo(
        () => (
            <>
                Mobile{' '}
                <b>
                    {new Intl.NumberFormat('en-GB', {
                        style: 'percent',
                        maximumFractionDigits: 1
                    }).format(mobileEngagementData.engagement)}
                </b>
            </>
        ),
        [mobileEngagementData.engagement]
    );

    const webEngagementText = React.useMemo(
        () => (
            <>
                Web App{' '}
                <b>
                    {new Intl.NumberFormat('en-GB', {
                        style: 'percent',
                        maximumFractionDigits: 1
                    }).format(webEngagementData.engagement)}
                </b>
            </>
        ),
        [webEngagementData.engagement]
    );

    return (
        <Box width="100%" mt={2}>
            <Grid container spacing={2}>
                {/* Total Ratings */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TotalsBox
                        measure={ratingsCountValues}
                        datePoints={datePoints}
                        title="Total Ratings"
                        format="number"
                        mainMetricFontSize="h3"
                        footer={
                            <>
                                {mobileEngagementData.loading && (
                                    <Box display="flex" gap={1} justifyContent="space-between">
                                        <Skeleton variant="text" width={80} height={30} />
                                        <Skeleton variant="text" width={80} height={30} />
                                    </Box>
                                )}
                                {!mobileEngagementData.loading && (
                                    <Box mt={1}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.6 }}>
                                            Engagement rate:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                            <EngagementText
                                                text={mobileEngagementText}
                                                tooltipTitle="Mobile Users Engagement Rate"
                                                tooltipText={`${mobileEngagementData.totalRatingsCount} Ratings,
                                                 ${mobileEngagementData.dismissals} Dismissals`}
                                            />
                                            <EngagementText
                                                text={webEngagementText}
                                                tooltipTitle="Web App Users Engagement Rate"
                                                tooltipText={`${webEngagementData.totalRatings} Ratings, ${webEngagementData.totalOrders} Orders`}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </>
                        }
                    />
                </Grid>

                {/* Average Rating */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TotalsBox
                        measure={averageRatingValues}
                        datePoints={datePoints}
                        title="Average Rating"
                        format="number"
                        differenceFormat="decimal"
                        mainMetricFontSize="h3"
                        footer={
                            <Box mt={0.5}>
                                {averageRatingValues?.current?.loading ? (
                                    <Skeleton height={26} width={100} />
                                ) : (
                                    <Rating
                                        value={averageRatingValues?.current?.value ?? 0}
                                        precision={0.1}
                                        readOnly
                                    />
                                )}
                            </Box>
                        }
                    />
                </Grid>

                {/* 5-Star Ratings */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TotalsBox
                        measure={totalFiveStarsValues}
                        datePoints={datePoints}
                        title="5-Star Ratings"
                        format="number"
                        mainMetricFontSize="h3"
                        footer={
                            <>
                                {fiveStarPercentageValues?.current?.loading && (
                                    <Skeleton height={26} width={100} />
                                )}
                                {!fiveStarPercentageValues?.current?.loading &&
                                    fiveStarPercentageValues?.current?.value > 0 && (
                                        <Row valign="center" style={{ marginTop: 4 }}>
                                            <StarIcon sx={{ fontSize: 20, color: STAR_COLOR, mr: 0.5 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600, color: STAR_COLOR }}
                                            >
                                                {new Intl.NumberFormat('en-GB', {
                                                    style: 'percent',
                                                    maximumFractionDigits: 1
                                                }).format(fiveStarPercentageValues?.current?.value)}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ ml: 0.5, opacity: 0.4, fontWeight: 600 }}
                                            >
                                                of all ratings
                                            </Typography>
                                        </Row>
                                    )}
                                {!fiveStarPercentageValues?.current?.loading &&
                                    fiveStarPercentageValues?.current?.value === 0 && (
                                        <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.6 }}>
                                            No 5-star ratings
                                        </Typography>
                                    )}
                            </>
                        }
                    />
                </Grid>

                {/* Rating Distribution */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard sx={{ pb: 0.5 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 'bold',
                                opacity: 0.75
                            }}
                        >
                            Rating Distribution
                        </Typography>
                        {ratingDistributionValues?.loading ? (
                            <Skeleton
                                variant="rectangular"
                                sx={{ borderRadius: 0.5 }}
                                width="100%"
                                height={100}
                            />
                        ) : (
                            <Box sx={{ width: '100%', height: 120 }}>
                                <Bar
                                    data={chartData}
                                    options={chartOptions}
                                    plugins={
                                        distributionPlugin as React.ComponentProps<typeof Bar>['plugins']
                                    }
                                />
                            </Box>
                        )}
                    </MetricCard>
                </Grid>
            </Grid>
        </Box>
    );
};
